import axiosInstance from '../helpers/axios';
import { officeConstants } from './constants';
import * as THREE from 'three';
import { STLLoader } from 'three-stdlib';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import {
    collectMergedGeometryAndMaterials as collectOptimizedGeometryAndMaterials,
    ensureIndexedGeometry,
    prepareGltfSourceForRuntime,
} from '../containers/FenceModel/ModelViewer/modelGeometryOptimization';
import {
    buildGeometryPerformanceMetrics,
    prepareGeometryForFastRaycast,
    registerSceneModelPerformance,
} from '../containers/FenceModel/ModelViewer/scenePerformance';

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'your-32-character-secret-key1234567890';

const MODEL_DEBUG =
    typeof window !== 'undefined'
        ? window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        : process.env.NODE_ENV !== 'production';

const debugLog = (...args) => {
    if (!MODEL_DEBUG) return;
    console.log(...args);
};


const PAID_ACCESS_CODES = new Set([
    'FEATURE_ACCESS_REQUIRED',
    'SUBSCRIPTION_REQUIRED',
    'OFFICE_SUBSCRIPTION_REQUIRED',
    'OFFICE_3D_SUBSCRIPTION_REQUIRED',
    'WAREHOUSE_SUBSCRIPTION_REQUIRED',
    'PRINT_FORM_SUBSCRIPTION_REQUIRED',
    'SCENE_PAYMENT_REQUIRED',
    'SCENE_ACCESS_REQUIRED',
    'SCENE_EDIT_ACCESS_EXPIRED',
    'OFFICE_BALANCE_REQUIRED',
    'USER_PRO_REQUIRED',
]);

const getRequestAccessCode = (error, fallbackCode = null) => {
    const data = error?.response?.data || {};
    const status = error?.response?.status;
    const message = String(data.message || data.error || error?.message || '').toLowerCase();

    if (data.code || data.errorCode || data.error_code) {
        return String(data.code || data.errorCode || data.error_code);
    }

    if ((status === 402 || status === 403) && message.includes('подпис')) {
        return fallbackCode || 'SUBSCRIPTION_REQUIRED';
    }

    return fallbackCode;
};

const isPaidAccessError = (error, code) => {
    if (code && PAID_ACCESS_CODES.has(code)) return true;

    const status = error?.response?.status;
    const message = String(error?.response?.data?.message || error?.response?.data?.error || error?.message || '').toLowerCase();

    return (status === 402 || status === 403) && message.includes('подпис');
};

const decryptData = async (modelPath, encryptedData) => {
    if (!encryptedData || !encryptedData.encrypted || !encryptedData.iv) {
        return null;
    }

    const { encrypted, iv } = encryptedData;

    try {
        const key = await crypto.subtle.importKey(
            'raw',
            new TextEncoder().encode(ENCRYPTION_KEY.padEnd(32, '\0').slice(0, 32)),
            { name: 'AES-CBC', length: 256 },
            false,
            ['decrypt']
        );

        const encryptedUint8 = new Uint8Array(atob(encrypted).split('').map((c) => c.charCodeAt(0)));
        const ivUint8 = new Uint8Array(atob(iv).split('').map((c) => c.charCodeAt(0)));

        const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-CBC', iv: ivUint8 },
            key,
            encryptedUint8
        );

        return decrypted;
    } catch (error) {
        debugLog('[ModelDebug][Decrypt][FAIL]', modelPath, error);
        return null;
    }
};

const getGeometryDimensions = (geometry) => {
    geometry.computeBoundingBox();
    const box = geometry.boundingBox;
    const size = new THREE.Vector3();
    box.getSize(size);

    return {
        x: size.x.toFixed(2),
        y: size.y.toFixed(2),
        z: size.z.toFixed(2),
    };
};

const summarizeMaterial = (mat, index = 0) => {
    if (!mat) {
        return {
            index,
            ok: false,
            name: 'null',
        };
    }

    return {
        index,
        ok: true,
        type: mat.type,
        name: mat.name || `mat_${index}`,
        hasMap: !!mat.map,
        hasNormalMap: !!mat.normalMap,
        hasRoughnessMap: !!mat.roughnessMap,
        hasMetalnessMap: !!mat.metalnessMap,
        hasAoMap: !!mat.aoMap,
        hasEmissiveMap: !!mat.emissiveMap,
        color: mat.color ? `#${mat.color.getHexString()}` : null,
        roughness: typeof mat.roughness === 'number' ? mat.roughness : null,
        metalness: typeof mat.metalness === 'number' ? mat.metalness : null,
    };
};

const patchMtlTexturePaths = (mtlText, textureUrls = {}) => {
    return String(mtlText || '')
        .split(/\r?\n/)
        .map((line) => {
            const trimmed = line.trim();
            if (!trimmed) return line;

            const tokens = trimmed.split(/\s+/);
            const cmd = tokens[0]?.toLowerCase();

            const isTextureCmd =
                cmd === 'map_kd' ||
                cmd === 'map_ka' ||
                cmd === 'map_ks' ||
                cmd === 'map_ke' ||
                cmd === 'map_bump' ||
                cmd === 'bump' ||
                cmd === 'norm' ||
                cmd === 'disp' ||
                cmd === 'decal' ||
                cmd === 'refl' ||
                cmd === 'map_d' ||
                cmd === 'map_pr' ||
                cmd === 'map_pm' ||
                cmd === 'map_ns';

            if (!isTextureCmd) return line;

            const lastToken = tokens[tokens.length - 1];
            const normalizedName = lastToken.split('/').pop().split('\\').pop();

            if (textureUrls[lastToken]) {
                tokens[tokens.length - 1] = textureUrls[lastToken];
                return tokens.join(' ');
            }

            if (textureUrls[normalizedName]) {
                tokens[tokens.length - 1] = textureUrls[normalizedName];
                return tokens.join(' ');
            }

            return line;
        })
        .join('\n');
};

const collectMergedGeometryAndMaterials = (root, label = 'MODEL') => {
    const result = collectOptimizedGeometryAndMaterials(root, {
        onMesh: ({ child, localMaterials, groups }) => {
            debugLog(`[ModelDebug][${label}][Mesh]`, {
                meshName: child.name || 'unnamed_mesh',
                materialCount: localMaterials.length,
                groupCount: groups.length,
                materials: localMaterials.map((material, index) => summarizeMaterial(material, index)),
            });
        },
        onGroup: ({ child, group, groupIndex, localMaterial, globalMaterialIndex, count }) => {
            debugLog(`[ModelDebug][${label}][GroupIndexed]`, {
                meshName: child.name || 'unnamed_mesh',
                groupIndex,
                localMaterialIndex: group.materialIndex,
                globalMaterialIndex,
                indexCount: count,
                material: summarizeMaterial(localMaterial, globalMaterialIndex),
            });
        },
    });

    debugLog(`[ModelDebug][${label}][MergedResult]`, {
        sourceMeshCount: result.sourceMeshCount,
        sourcePrimitiveCount: result.sourcePrimitiveCount,
        mergedGroups: result.geometry.groups?.length || 0,
        indexed: !!result.geometry.index,
        vertices: result.geometry.getAttribute('position')?.count || 0,
        indices: result.geometry.index?.count || 0,
        materialsCount: result.materials.length,
        materials: result.materials.map((material, index) => summarizeMaterial(material, index)),
    });

    return {
        geometry: result.geometry,
        materialsArray: result.materials,
        sourceMeshCount: result.sourceMeshCount,
        sourcePrimitiveCount: result.sourcePrimitiveCount,
    };
};

export const setCurrentOfficeId = (officeId) => {
    return {
        type: officeConstants.SET_CURRENT_OFFICE,
        payload: officeId || null,
    };
};

export const getProductModelData = (officeId, productId, options = {}) => {
    return async (dispatch, getState) => {
        let objUrl;
        let mtlUrl;
        let textureUrls = {};

        try {
            const state = getState?.();
            let fullName = '';

            try {
                const lists = Object.values(state?.office?.productsByCategory || {});
                for (const list of lists) {
                    const p = (list || []).find((x) => x?._id === productId);
                    if (p) {
                        fullName = p.fullName || '';
                        break;
                    }
                }
            } catch (e) { }

            dispatch({
                type: officeConstants.GET_PRODUCT_MODEL_DATA_PROGRESS,
                payload: { productId, loaded: 0, total: 1, fullName },
            });

            dispatch({ type: officeConstants.GET_PRODUCT_MODEL_DATA_REQUEST });

            const cleanSalesSessionId = String(options.salesSessionId || '').trim().toUpperCase();
            const participantToken = String(options.participantToken || '').trim();
            const usePublicSalesSessionModel = options.publicAccess === true &&
                !!officeId &&
                !!cleanSalesSessionId &&
                !!participantToken;
            const requestUrl = usePublicSalesSessionModel
                ? `/office-sales-session/public/${officeId}/${cleanSalesSessionId}/model/${productId}`
                : `/office/product-model/${productId}`;

            const response = await axiosInstance.get(requestUrl, {
                params: usePublicSalesSessionModel ? undefined : { officeId },
                sceneGuestToken: usePublicSalesSessionModel ? participantToken : undefined,
                onDownloadProgress: (e) => {
                    const loaded = Number(e?.loaded) || 0;
                    const total = Number(e?.total) || loaded || 1;

                    dispatch({
                        type: officeConstants.GET_PRODUCT_MODEL_DATA_PROGRESS,
                        payload: { productId, loaded, total, fullName },
                    });
                },
            });

            if (!response || !response.data) {
                throw new Error(`Сервер вернул пустой ответ для ${productId}`);
            }

            const encryptedData = response.data.encryptedData;
            const product = response.data.product && typeof response.data.product === 'object'
                ? response.data.product
                : null;
            const modelPath = productId;

            debugLog('[ModelDebug][Loader][Response]', {
                productId,
                fileType: encryptedData?.fileType,
                hasEncryptedData: !!encryptedData,
                hasMtl: !!encryptedData?.mtl,
                textureKeys: Object.keys(encryptedData?.textures || {}),
            });

            const buffer = await decryptData(modelPath, encryptedData);

            if (!buffer) {
                throw new Error(`Не удалось расшифровать модель ${modelPath}`);
            }

            const fileType = (encryptedData.fileType || 'stl').toLowerCase();
            const decodeStartedAt = performance.now();
            let decodeFinishedAt = decodeStartedAt;
            let mergeStartedAt = decodeStartedAt;
            let geometry = null;
            let dimensions = null;
            let materials = null;
            let sourceMeshCount = 1;
            let sourcePrimitiveCount = 1;

            debugLog('[ModelDebug][Loader][Decrypted]', {
                productId,
                fileType,
                byteLength: buffer?.byteLength || 0,
            });

            if (fileType === 'stl') {
                const loader = new STLLoader();
                geometry = loader.parse(buffer);
                if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
                ensureIndexedGeometry(geometry);
                decodeFinishedAt = performance.now();
                mergeStartedAt = decodeFinishedAt;
                dimensions = getGeometryDimensions(geometry);

                debugLog('[ModelDebug][Loader][STL]', {
                    productId,
                    dimensions,
                });
            } else if (fileType === 'obj') {
                const mtlData = encryptedData.mtl
                    ? await decryptData(`${modelPath}.mtl`, encryptedData.mtl)
                    : null;

                const textures = encryptedData.textures || {};
                objUrl = URL.createObjectURL(new Blob([buffer], { type: 'text/plain' }));

                for (const [name, textureData] of Object.entries(textures)) {
                    const textureBuffer = await decryptData(`${modelPath}-${name}`, textureData);

                    if (textureBuffer) {
                        const ext = name.split('.').pop()?.toLowerCase() || 'png';
                        const mime =
                            ext === 'jpg' || ext === 'jpeg'
                                ? 'image/jpeg'
                                : ext === 'webp'
                                    ? 'image/webp'
                                    : 'image/png';

                        const textureBlob = new Blob([textureBuffer], { type: mime });
                        textureUrls[name] = URL.createObjectURL(textureBlob);
                    }
                }

                debugLog('[ModelDebug][Loader][OBJ][TexturesPrepared]', {
                    productId,
                    textureKeysFromServer: Object.keys(textures),
                    textureBlobKeys: Object.keys(textureUrls),
                });

                const mtlLoader = new MTLLoader();
                const objLoader = new OBJLoader();

                if (mtlData) {
                    let mtlText = new TextDecoder().decode(mtlData);
                    const beforePatch = mtlText;
                    mtlText = patchMtlTexturePaths(mtlText, textureUrls);

                    mtlUrl = URL.createObjectURL(new Blob([mtlText], { type: 'text/plain' }));

                    debugLog('[ModelDebug][Loader][OBJ][MTL Patch]', {
                        productId,
                        changed: beforePatch !== mtlText,
                        sampleBefore: beforePatch.slice(0, 500),
                        sampleAfter: mtlText.slice(0, 500),
                    });

                    materials = await new Promise((resolve) => {
                        const parsed = mtlLoader.parse(mtlText, '');
                        parsed.preload();
                        resolve(parsed);
                    });

                    debugLog('[ModelDebug][Loader][OBJ][MaterialsParsed]', {
                        productId,
                        materialNames: Object.keys(materials?.materials || {}),
                        materials: Object.values(materials?.materials || {}).map((mat, index) => summarizeMaterial(mat, index)),
                    });

                    objLoader.setMaterials(materials);
                }

                const object = await new Promise((resolve, reject) => {
                    objLoader.load(objUrl, resolve, undefined, reject);
                });

                decodeFinishedAt = performance.now();
                mergeStartedAt = decodeFinishedAt;

                const mergedData = collectMergedGeometryAndMaterials(object, 'OBJ');
                geometry = mergedData.geometry;
                materials = { materialsArray: mergedData.materialsArray };
                sourceMeshCount = mergedData.sourceMeshCount;
                sourcePrimitiveCount = mergedData.sourcePrimitiveCount;
                dimensions = getGeometryDimensions(geometry);

                debugLog('[ModelDebug][Loader][OBJ][Final]', {
                    productId,
                    dimensions,
                    materialsCount: materials.materialsArray.length,
                    materials: materials.materialsArray.map((mat, index) => summarizeMaterial(mat, index)),
                });
            } else if (fileType === 'glb' || fileType === 'gltf') {
                const gltfLoader = new GLTFLoader();

                // +++ ПОДГОТОВКА DRACO DECODER +++
                const dracoLoader = new DRACOLoader();
                const cores = typeof navigator !== 'undefined'
                    ? Math.max(1, Number(navigator.hardwareConcurrency || 4))
                    : 4;

                dracoLoader.setDecoderPath('/draco/');
                dracoLoader.setWorkerLimit(Math.max(1, Math.min(4, Math.floor(cores / 2))));
                dracoLoader.preload();
                gltfLoader.setDRACOLoader(dracoLoader);

                const rawGltfSource =
                    fileType === 'gltf'
                        ? new TextDecoder().decode(buffer)
                        : buffer;
                const gltfSource = prepareGltfSourceForRuntime(rawGltfSource, fileType);

                const gltf = await new Promise((resolve, reject) => {
                    gltfLoader.parse(gltfSource, '', resolve, reject);
                });

                // Очищаем воркеры из памяти после парсинга
                dracoLoader.dispose();

                const rawSceneInfo = [];
                gltf.scene.traverse((child) => {
                    if (!child?.isMesh) return;

                    const mats = Array.isArray(child.material) ? child.material : [child.material];
                    rawSceneInfo.push({
                        meshName: child.name || 'unnamed_mesh',
                        materialCount: mats.length,
                        groupCount: child.geometry?.groups?.length || 0,
                        materials: mats.map((mat, index) => summarizeMaterial(mat, index)),
                    });
                });

                debugLog('[ModelDebug][Loader][GLTF][BeforeMerge]', {
                    productId,
                    sceneChildren: rawSceneInfo,
                });

                decodeFinishedAt = performance.now();
                mergeStartedAt = decodeFinishedAt;

                const mergedData = collectMergedGeometryAndMaterials(gltf.scene, 'GLTF');
                geometry = mergedData.geometry;
                materials = { materialsArray: mergedData.materialsArray };
                sourceMeshCount = mergedData.sourceMeshCount;
                sourcePrimitiveCount = mergedData.sourcePrimitiveCount;
                dimensions = getGeometryDimensions(geometry);

                debugLog('[ModelDebug][Loader][GLTF][Final]', {
                    productId,
                    dimensions,
                    mergedGroupCount: geometry.groups?.length || 0,
                    materialsCount: materials.materialsArray.length,
                    materials: materials.materialsArray.map((mat, index) => summarizeMaterial(mat, index)),
                });
            } else {
                throw new Error(`Unsupported file type: ${fileType}`);
            }

            const finishedAt = performance.now();
            const performanceMetrics = buildGeometryPerformanceMetrics({
                geometry,
                modelId: productId,
                fileType,
                sourceBytes: Number(buffer.byteLength || 0),
                decodeMs: decodeFinishedAt - decodeStartedAt,
                mergeMs: finishedAt - mergeStartedAt,
                materialCount: Array.isArray(materials?.materialsArray)
                    ? materials.materialsArray.length
                    : 0,
                sourceMeshCount,
                sourcePrimitiveCount,
            });

            registerSceneModelPerformance(productId, performanceMetrics);
            prepareGeometryForFastRaycast(geometry, {
                modelId: productId,
                defer: performanceMetrics.triangles >= 30000,
                maxLeafTris: performanceMetrics.triangles > 500000 ? 48 : 24,
            });

            const action = {
                type: officeConstants.GET_PRODUCT_MODEL_DATA_SUCCESS,
                payload: {
                    productId,
                    geometry,
                    materials: materials ? materials.materialsArray : null,
                    dimensions,
                    textures: encryptedData.textures || [],
                    fileType,
                    performance: performanceMetrics,
                    product,
                },
            };

            debugLog('[ModelDebug][Loader][DispatchSuccess]', {
                productId,
                fileType,
                dimensions,
                materialsCount: action.payload.materials?.length || 0,
                geometryGroupCount: action.payload.geometry?.groups?.length || 0,
                modelPartsEnabled: action.payload.product?.modelPartsEnabled === true,
            });

            dispatch(action);
            return action;
        } catch (error) {
            const errorData = error?.response?.data || {};
            const status = error?.response?.status || null;
            const code = getRequestAccessCode(error, status === 402 ? 'OFFICE_3D_SUBSCRIPTION_REQUIRED' : null);
            const message = errorData.message || errorData.error || error.message || 'Ошибка загрузки модели';
            const blocked = isPaidAccessError(error, code);

            debugLog('[ModelDebug][Loader][FAIL]', {
                productId,
                status,
                code,
                blocked,
                message,
                stack: error?.stack,
            });

            const failAction = {
                type: officeConstants.GET_PRODUCT_MODEL_DATA_FAILURE,
                payload: {
                    productId,
                    error: message,
                    message,
                    status,
                    code,
                    blocked,
                },
            };

            dispatch(failAction);
            throw error;
        } finally {
            dispatch({
                type: officeConstants.CLEAR_PRODUCT_MODEL_MODEL_PROGRESS,
                payload: { productId },
            });

            if (objUrl) URL.revokeObjectURL(objUrl);
            if (mtlUrl) URL.revokeObjectURL(mtlUrl);
        }
    };
};


const buildOfficeRequestData = (officeData = {}) => {
    const formData = new FormData();

    const appendText = (key, value) => {
        if (value === undefined || value === null) return;
        formData.append(key, String(value));
    };

    appendText("name", officeData.name);
    appendText("address", officeData.address);
    appendText("accentColor", officeData.accentColor);

    if (Array.isArray(officeData.categories)) {
        formData.append("categories", JSON.stringify(officeData.categories));
    }

    if (Array.isArray(officeData.allowedUsers)) {
        formData.append("allowedUsers", JSON.stringify(officeData.allowedUsers));
    }

    if (typeof File !== "undefined" && officeData.coverImageFile instanceof File) {
        formData.append("coverImage", officeData.coverImageFile);
    }

    if (typeof File !== "undefined" && officeData.watermarkImageFile instanceof File) {
        formData.append("watermarkImage", officeData.watermarkImageFile);
    }

    if (officeData.removeCoverImage === true) {
        formData.append("removeCoverImage", "true");
    }

    if (officeData.removeWatermarkImage === true) {
        formData.append("removeWatermarkImage", "true");
    }

    return formData;
};

export const createOffice = (officeData) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.CREATE_OFFICE_REQUEST });
            const response = await axiosInstance.post('/office/create', buildOfficeRequestData(officeData));
            dispatch({ type: officeConstants.CREATE_OFFICE_SUCCESS, payload: { office: response.data.office } });
            return response.data;
        } catch (error) {
            dispatch({ type: officeConstants.CREATE_OFFICE_FAILURE, payload: { error: error.response?.data?.message || 'Ошибка' } });
            throw error;
        }
    };
};

export const getOffices = () => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.GET_OFFICES_REQUEST });
            const response = await axiosInstance.get('/office/get');
            dispatch({ type: officeConstants.GET_OFFICES_SUCCESS, payload: { offices: response.data.offices } });
            return response.data;
        } catch (error) {
            dispatch({ type: officeConstants.GET_OFFICES_FAILURE, payload: { error: error.response?.data?.message || 'Ошибка' } });
            throw error;
        }
    };
};

export const updateOffice = (officeId, officeData) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.UPDATE_OFFICE_REQUEST });
            const response = await axiosInstance.put(`/office/update/${officeId}`, buildOfficeRequestData(officeData));
            dispatch({ type: officeConstants.UPDATE_OFFICE_SUCCESS, payload: { office: response.data.office } });
            return response.data;
        } catch (error) {
            dispatch({ type: officeConstants.UPDATE_OFFICE_FAILURE, payload: { error: error.response?.data?.message || 'Ошибка' } });
            throw error;
        }
    };
};

export const getOfficeCategories = (officeId) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.GET_OFFICE_CATEGORIES_REQUEST });
            const response = await axiosInstance.get(`/office/categories/${officeId}`);
            dispatch({ type: officeConstants.GET_OFFICE_CATEGORIES_SUCCESS, payload: { categories: response.data.categories } });
        } catch (error) {
            dispatch({ type: officeConstants.GET_OFFICE_CATEGORIES_FAILURE, payload: { error: error.response?.data?.message || 'Ошибка' } });
        }
    };
};

export const getProductsByCategory = (officeId, categorySlug, page = 1, limit = 100) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.GET_PRODUCTS_BY_CATEGORY_REQUEST });
            const response = await axiosInstance.get(`/office/products/${officeId}`, { params: { categorySlug, page, limit } });
            dispatch({
                type: officeConstants.GET_PRODUCTS_BY_CATEGORY_SUCCESS,
                payload: { categorySlug, products: response.data.products, pagination: response.data.pagination },
            });
        } catch (error) {
            dispatch({ type: officeConstants.GET_PRODUCTS_BY_CATEGORY_FAILURE, payload: { error: error.response?.data?.message || 'Ошибка' } });
        }
    };
};

export const updateProductPrice = (officeId, productId, priceData) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.UPDATE_PRODUCT_PRICE_REQUEST });
            const response = await axiosInstance.put(`/office/${officeId}/product/${productId}/price`, priceData);
            dispatch({ type: officeConstants.UPDATE_PRODUCT_PRICE_SUCCESS, payload: { product: response.data.product } });
        } catch (error) {
            dispatch({ type: officeConstants.UPDATE_PRODUCT_PRICE_FAILURE, payload: { error: error.response?.data?.message || 'Ошибка' } });
        }
    };
};
